/**
 * Decisions, read.
 *
 * Writing one is not here: a decision is created by elevating an answer, which
 * lives in `features/chat/chat-api.ts` because that is the act it belongs to.
 * That file already invalidates this list's key on success — the two halves are
 * apart on purpose, and this is the seam where they meet.
 */
import { useQuery } from "@tanstack/react-query";
import type { Decision } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";

export const decisionKeys = {
  all: (projectId: string) => ["project", projectId, "decisions"] as const,
  /** The term is part of the key, so a search is a new query rather than a
   *  refetch of the same one — which keeps the previous results on screen
   *  while the next land instead of flashing empty. */
  list: (projectId: string, q: string) =>
    ["project", projectId, "decisions", "list", { q }] as const,
};

export function useDecisions(projectId: string | undefined, q = "") {
  return useQuery({
    queryKey: decisionKeys.list(projectId ?? "", q),
    enabled: Boolean(projectId),
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<{ decisions: Decision[]; total: number }> => {
      const res = await api.decisions.list({
        params: { id: projectId! },
        query: q ? { q } : {},
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return { decisions: res.body.decisions, total: res.body.total };
    },
  });
}
